import React from "react";
import { 
  UserPlus, 
  CheckCircle, 
  Activity, 
  Star 
} from "lucide-react";

const EngagementFunnel = ({ funnelData, loading }) => {
  // Calculate conversion rates safely
  const getConversionRate = (numerator, denominator) => {
    if (!denominator || !numerator) return 0;
    return Math.round((numerator / denominator) * 100);
  };

  const checkedInRate = getConversionRate(funnelData?.checkedIn, funnelData?.registered);
  const activeRate = getConversionRate(funnelData?.active, funnelData?.registered);
  const highlyEngagedRate = getConversionRate(funnelData?.highlyEngaged, funnelData?.registered);

  if (loading) {
    return (
      <div className="funnel-container" aria-busy="true" aria-label="Loading engagement funnel">
        <h2 className="funnel-title">Engagement Funnel</h2>
        <div className="funnel-steps-skeleton">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="funnel-step loading">
              <div className="funnel-step-icon-skeleton" />
              <div className="funnel-step-title-skeleton" />
              <div className="funnel-step-value-skeleton" />
            </div>
          ))}
        </div>
        <div className="funnel-progress-skeleton">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="funnel-bar-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="funnel-container" aria-label="User engagement funnel visualization">
      <h2 className="funnel-title">Engagement Funnel</h2>
      
      {/* Steps Container - Responsive Grid */}
      <div className="funnel-steps">
        {[
          { icon: <UserPlus size={24} aria-hidden="true" />, label: "Registered", value: funnelData?.registered || 0, color: "#f97316" },
          { icon: <CheckCircle size={24} aria-hidden="true" />, label: "Checked In", value: funnelData?.checkedIn || 0, color: "#10b981" },
          { icon: <Activity size={24} aria-hidden="true" />, label: "Active (2+ sessions)", value: funnelData?.active || 0, color: "#3b82f6" },
          { icon: <Star size={24} aria-hidden="true" />, label: "Highly Engaged", value: funnelData?.highlyEngaged || 0, color: "#f59e0b" }
        ].map((step, index) => (
          <div 
            key={index} 
            className="funnel-step"
            aria-label={`${step.label}: ${step.value} users`}
          >
            <div className="funnel-step-icon" style={{ backgroundColor: `${step.color}15`, color: step.color }}>
              {step.icon}
            </div>
            <div className="funnel-step-title">{step.label}</div>
            <div className="funnel-step-value">{step.value.toLocaleString()}</div>
          </div>
        ))}
      </div>
      
      {/* Progress Bars - Full Width on Mobile */}
      <div className="funnel-progress" aria-label="Conversion rates from registered users">
        {[
          { label: "Checked In", value: funnelData?.checkedIn || 0, rate: checkedInRate, color: "#10b981" },
          { label: "Active", value: funnelData?.active || 0, rate: activeRate, color: "#3b82f6" },
          { label: "Highly Engaged", value: funnelData?.highlyEngaged || 0, rate: highlyEngagedRate, color: "#f59e0b" }
        ].map((bar, index) => (
          <div key={index} className="funnel-bar-container">
            <div className="funnel-bar-label-container">
              <span className="bar-label">{bar.label}</span>
              <span className="bar-value">
                {bar.value.toLocaleString()} ({bar.rate}%)
              </span>
            </div>
            <div 
              className="funnel-bar" 
              role="progressbar"
              aria-valuenow={bar.rate}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label={`${bar.label} conversion rate`}
            >
              <div 
                className="funnel-bar-fill" 
                style={{ 
                  width: `${Math.min(bar.rate, 100)}%`,
                  backgroundColor: bar.color 
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EngagementFunnel;